/* @flow */
import moment from 'moment-timezone';
import crypto from 'crypto';
import { Map, List, Record, OrderedMap } from 'immutable';
import { Iterable } from 'immutable';
import isEmpty from 'ramda/src/isEmpty';
import prop from 'ramda/src/prop';
import forEach from 'ramda/src/forEach';
import {compose, pickBy, is, keys} from 'ramda';
import isString from 'lodash.isstring';
import escapeRegExp from 'lodash.escaperegexp';
import createDebugger from 'debug';
import { capitalize, toLower } from 'trustar/utils';
import { IO } from 'trustar/fp';
import { parse as parseURL } from 'url';

const debug = createDebugger('app:lib:utils');

export const isReport = (map: Map<any, any>): boolean => {
  if (!map && typeof map !== 'object') return false;
  return map.has && map.has('title');
};

export const formatArrayValues = (valueArray: Array<*> = []) => {
  const arrayString = valueArray.toString();
  return arrayString.replace(/\,/g, ', '); /* Todd,James -> Todd, James */
};


export const getHostname = (x: string): ?string =>
  IO.of(x)
    .map(parseURL)
    .map(prop('hostname'))
    .foldOr(err => {
      console.warn('Cannot parse malformed or undefined URL: ', x);
      return null;
    });

/**
 * Returns a plucker function that will pluck the property at a given keypath
 * from an immutable iterable.
 */
export const pluck = (keyPath: string | string[], notSetValue?: any) => (x: Iterable<*, *>) => {
  if (typeof keyPath === 'string') keyPath = [keyPath];
  return x.getIn(keyPath, notSetValue);
};

export const pick = (...properties: string[]) => (x: Map<*, *>) => {
  return properties.reduce((map, prop) => {
    if (typeof prop === 'string') prop = [prop];

    // Do not assign any props that aren't in the source
    if (!x.hasIn(prop)) return map;

    return map.set(prop, x.getIn(prop));
  }, Map());
};

/**
 * Calculate a diff between two objects. Only calculates shallow diffs.
 * Sometimes useful for debugging prop or state updates.
 *
 * Example:
 *  const propDiff = diff(prevProps, this.props);
 *  if (propDiff) console.log(propDiff);
 */
export const diff = (a: Object, b: Object) => {
  const results = {};
  forEach((k) => {
    if (a[k] !== b[k]) {
      results[k] = {
        prev: a[k],
        current: b[k],
      };
    }
  })(Object.keys(a));

  return isEmpty(results) ? null : results;
};

type DimensionsShape = { width: number, height: number };
export const getWindowDimensions = (): DimensionsShape => {
  let result = { width: 0, height: 0 };

  // NOTE: This is all in a try catch so we ignore flow errors
  try {
    const width = window.innerWidth
      // $FlowIgnore
      || document.documentElement.clientWidth
      // $FlowIgnore
      || document.body.clientWidth;

    const height = window.innerHeight
      // $FlowIgnore
      || document.documentElement.clientHeight
      // $FlowIgnore
      || document.body.clientHeight;

    result = { width, height };
  } catch (err) {
    /* Hopefully this never happens */
    debug('Could not get window dimensions.', err);
  }

  return result;
};

/**
 * Redirect to a new URL, however, if in dev mode just warn and do nothing else.
 * This is useful for pages that auto redirect. We sometimes need to debug them
 * and it's a real annoyance to have to create a new user or get the app into
 * some difficult to access state.
 */
export const redirectTo = (url: string, isDev: ?boolean) => {
  if (isDev) {
    debug(`redirectTo('${url}') called but dev mode enabled. Not redirecting`);
    return;
  }

  window.location.replace(url);
};

/**
 * Pluralize a word.
 * NOTE: This does NOT account for the numerous odd pluralizations in the
 * english language (yet)
 */
export const pluralize = (word: string, count: number): string => {
  return count === 1 ? word : `${word}s`;
};

/**
 * Given some text truncate it to within a given length, adding elipses to show
 * that it has been truncated.
 */
type TruncateText = (a: { text: string, length?: number }) => string;
export const truncateText: TruncateText = ({ text = '', length = 24 }) => {
  if (!isString(text)) {
    debug(`truncateText passed non-text value of ${text}`);
    return '';
  }
  if (text.length <= length) return text;
  return text.slice(0, length - 3) + '...';
};

/**
 * Convert bytes to KB
 */
export const bytesToKb = (bytes: number) => {
  return bytes / 1024;
};

/**
 * Convert bytes to MB
 */
export const bytesToMb = (bytes: number) => bytesToKb(bytes) / 1024;

export const kbToBytes = (kb: number) => kb * 1024;

export const mbToBytes = (mb: number) => kbToBytes(mb) * 1024;

export const formatFilesize = (bytes: number) => {
  const kb = bytesToKb(bytes);
  const mb = bytesToMb(bytes);
  return kb < 1000 ? `${kb.toFixed(2)} kb` : `${mb.toFixed(2)} mb`;
};

/**
 * Asynchronously read a file. This is simply a wrapper around the FileReader
 * API, which is not great.
 */
export const readFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    if (!(file instanceof Blob)) {
      const err = new Error('file must be an instance of File or Blob.');
      return reject(err);
    }

    reader.onerror = (...args) => reject(...args);
    reader.onabort = (...args) => reject(...args);

    // reader.result should be a string. See the docs:
    // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsText
    // $FlowIssue
    reader.onloadend = contents => resolve(reader.result);

    reader.readAsText(file);
  });
};

/**
 * The formatting portion of this function is pretty arbitrary and not based on
 * anything. It may prove better to simply use a separate function rather than
 * add formatting logic to this one.
 *
 * If format is passed then the '%%' within the format string will be replaced
 * with the relative date.
 *
 * NOTE: we are calling this funciton on a number of types of objects througout
 * the app, not all of which are guaranteed to have a `created` property. As
 * such we short circuit and return 'Unknown', preserving the implied return
 * type of String. This isn't the prettiest solution but without stricter
 * contracts on the server side we can't make many assumptions.
 */
type Created = moment | number;
export const formatRelativeTime = (created: ?Created, format: ?string): string => {
  if (Number.isFinite(created)) {
    created = moment(created);
  }

  // Short circuit if we aren't passed a moment. See note above for details.
  if (!moment.isMoment(created)) {
    debug('formatRelativeTime should be passed a moment object. It was not. Instantiating new moment...');
    return 'Unknown';
  }

  // Short circuit if a format was passed. Just use moment directly
  if (format) {
    // $FlowIssue: created is definitely an instance of moment here
    return created.utc().format(format);
  }

  const now = moment();
  const ms = now.diff(created);
  const d = moment.duration(ms);
  const minutes = Math.floor(d.asMinutes());
  const hours = Math.floor(d.asHours());
  const days = Math.floor(d.asDays());
  const weeks = Math.floor(d.asWeeks());
  const months = Math.floor(d.asMonths());
  let relative;

  if (minutes < 1) {
    relative = 'just now';
  } else if (minutes < 60) {
    relative = `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (hours < 24) {
    relative = `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (days < 31) {
    relative = `${days} day${days !== 1 ? 's' : ''} ago`;
  } else if (weeks < 4) {
    relative = `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  } else if (months < 12) {
    relative = `${months} month${months !== 1 ? 's' : ''} ago`;
  } else {
    // $FlowIssue: created is definitely an instance of moment here
    relative = created.format('YYYY-MM-DD HH:mm:ss');
  }

  return relative;
};

export const toCaps = (str: string): string =>
  toLower(str)
    .split(' ')
    .map(capitalize)
    .join(' ');

/* **************************************************************************
 * Report Parsing
 * *************************************************************************/

export const shasum = (string: string) => {
  const _shasum = crypto.createHash('sha1');
  _shasum.update(string);
  return _shasum.digest('hex');
};

/**
 * Given a string, replace every substring that is matched by the `match` regex
 * with the result of calling `fn` on matched substring. The result will be an
 * array with all odd indexed elements containing the replacements. The primary
 * use case is similar to using String.prototype.replace execpt for React.
 *
 * React will happily render an array as children of a react element, which
 * makes this approach very useful for tasks like surrounding certain text
 * within a string with react elements.
 *
 * Example:
 * matchReplace(
 *   'Emphasize all phone numbers like 884-555-4443.',
 *   /([\d|-]+)/g,
 *   (number, i) => <strong key={i}>{number}</strong>
 * );
 * // => ['Emphasize all phone numbers like ', <strong>884-555-4443</strong>, '.'
 *
 * @param {string} str
 * @param {regexp} match Must contain a matching group
 * @param {function} fn
 * @return {array}
 */

type MatchReplace = (a: string, b: RegExp, fn: (x: any, i: number) => any) => any[];
export const matchReplace: MatchReplace = (str, match, fn) => {
  const result = str.split(match);

  // Apply fn to all odd elements
  for (let i = 1, length = result.length; i < length; i += 2) {
    result[i] = fn(result[i], i);
  }

  return result;
};

/**
 * A simple dictionary for storing redactable values and accessing them later by
 * their hash. The reason for this is that initially we were running into issues
 * with double redaction because certain certain strings, especially numbers,
 * would be redacted multiple times.
 *
 * For example: 'ip 8.8.8.8' was initially transformed to 'ip {catID::8.8.8.8}'
 * but then at some later time some other redactable would have text that also
 * matched 8.8.8.8 or perhaps a subset. In the end we would end up with nested
 * redactable string entities which just doens't work:
 * 'ip {catID::8.8.{catID::8.8}}'
 * By hashing the redactable text string we can replace it with a unique hash
 * that won't be matched by subsequent redactables:
 * 'ip {catID::8.8.8.8}' -> 'ip {catID::c4c36a91b52b495ff25b50a041908b2fddb8f89e}'
 *
 * NOTE: This is a IIFE that returns an object. i.e. redactableDict is an
 * object, not a function.
 *
 * TODO: THis should be moved into state. Also since there are both get and set
 * methods exposed
 */
export const redactableDict = (() => {
  let dict = {};

  return {
    get(k: string) { return dict[k]; },
    set(k: string, v: any) { return (dict[k] = v); },
    clear() { dict = {}; },
  };
})(); // IIFE

export const redactedDict = (() => {
  let dict = {};

  return {
    get(k: string) { return dict[k]; },
    set(k: string, v: any) { return (dict[k] = v); },
    clear() { dict = {}; },
  };
})(); // IIFE

/**
 * Wrap all redactable text strings in an easily matchable custom string format
 * that contains the type of redactable as well as the text value. By
 * pre-parsing the string this way we make it very simple to match all these
 * string entities with a regex and perform react string -> array replacement
 * for rendering.
 *
 * The reasoning is that this makes matchReplace work quite well. If we wanted
 * to skip directly to match replace we would likely end up creating many nested
 * arrays. By iterating through the redactables this way and building a parsed
 * entity string we are able to pass the resulting string directly to
 * matchReplace, rather than calling matchReplace on a number of strings within
 * an array, potentially producing even deeper nested arrays.
 *
 * IMPORTANT NOTE: We do not use case-insensitive regex because currently the
 * server will return things like 'password' and 'Password' separately.
 * NOTE: If we can figure out a way to never match strings within brackets then
 * this will not be necessary.
 *
 * Example:
 * ('hey 8.8.8.8', [{ categoryId: 5, text: '8.8.8.8' }]) -> 'hey {5::8.8.8.8}'
 */
export const parseRedactables = (str: string, redactables: List<Map<any, any>>) => {
  let result = str;

  redactables.forEach(x => {
    const categoryId = x.get('categoryId');
    const text = x.get('text');
    const hash = shasum(text);

    redactableDict.set(hash, text); // Store the real value for later

    const re = new RegExp(escapeRegExp(text), 'g'); // See important note above
    result = result.replace(re, `{-{${categoryId}::${hash}}-}`);
  });

  return result;
};


/**
 * NOTE: ESLint flagged this escaping as uesless but I'm not sure it actually
 * is.
 */
const reFromPattern = (pattern) => {
  return escapeRegExp(pattern).replace(/(\\\*)/gi, '\(\\S\*\)'); // eslint-disable-line no-useless-escape
};

/**
* Wrap all REDACTED text strings in an easily matchable custom string format
* that contains the type of redactable as well as the text value.
*
* NOTE: The toHash paramter is a boolean that if true, will replace all
* occurrences of the redacted terms with a hash in the form
* {-categoryName::text::hash-}, so that these terms can be rendered specially
* in the UI. When toHash is false, the redacted terms are replaced with the
* category they belong in, in the form <categoryName>, so that these terms
* will be replaced in the report when saved or submitted.
* @param {string} str
* @param {Immutable.List<Immutable.Map>} redactables
* @param {bool} toHash
* @return {string}
*/
export const parseRedacted = (str: string, redactionMap: Map<any, any>, toHash: boolean): string => {
  if (!isString(str)) return str;

  let result = str;

  const [ redactables, rejectedRedactables ] = redactionMap.reduce((agg, x) => {
    const type = x.get('type');

    if (type === 'TEXT' || type === 'REGEX') {
      agg[0].push(x); // Redactable
    } else {
      agg[1].push(x); // Rejected
    }

    return agg;
  }, [[], []]);

  rejectedRedactables.forEach(x => {
    let type = 'unknown';
    let text = 'unknown';

    if (x && typeof x.toJS === 'function') {
      type = x.get('type');
      text = x.get('text');
    }

    debug(`WARNING: Unparsable redaction term found: { type: ${type}, text: ${text} }`);
  });

  redactables.forEach(x => {
    const categoryId = x.get('categoryId');
    const categoryName = x.get('categoryName');
    const text = x.get('text');
    const type = x.get('type', '').toUpperCase();
    const hash = shasum(text);

    redactedDict.set(hash, text); // Store the real value for later

    let regex;

    if (type === 'TEXT') {
      const regString = reFromPattern(text);

      const first = (text.length > 0) ? text.charAt(0) : text;
      const last = (text.length > 0) ? text.charAt(text.length - 1) : text;
      const punctuation = new RegExp(/[^\w\s]/);

      if (first.match(punctuation) && last.match(punctuation)) { // ?text?
        regex = new RegExp(regString, 'gi');
      } else if (first.match(punctuation)) { // ?text
        regex = new RegExp(`(${regString})(\\b|(?=\\_))`, 'gi');
      } else if (last.match(punctuation)) { // text?
        regex = new RegExp(`(\\b|(?:\\_))(${regString})`, 'gi');
      } else { // text OR te?xt
        regex = new RegExp(`(\\b|(?:\\_))(${regString})(\\b|(?=\\_))`, 'gi');
      }
    } else if (type === 'REGEX') {
      const regString = reFromPattern(text);
      regex = new RegExp(`(\\b|(?:\\_))${regString}(\\b|(?=\\_))`, 'gi');
      // This is disgustingly hacky code just to get the IP addresses that are
      // redacted to be valid. The necessity for this edge case handling comes
      // from our lack of support for CIDR blocks in wildcard redaction -- this
      // leads users to input terms such as 10.* but our usual regex is not
      // sufficent in accurately capturing the valid IP, not just substrings.
      if (categoryName === 'ip-address') {
        const matches = result.match(regex);
        if (matches) {
          const validMatches = matches
              .filter(mat => /([0-9]{1,3}\.){3}[0-9]{1,3}/.test(mat))
              .map(mat => escapeRegExp(mat));
          if (validMatches.length > 0) {
            const validRegex = new RegExp(validMatches.join('|'), 'gi');
            result = toHash
              ? result.replace(validRegex, `{--{${categoryId}::${hash}}--}`)
              : result.replace(validRegex, `<${categoryName}>`);
            return result;
          }
        }
        return result;
      }
    }

    if (regex) {
      result = toHash
        ? result.replace(regex, `{--{${categoryId}::${hash}}--}`)
        : result.replace(regex, `<${categoryName}>`);
    }

    return result;
  });

  return result;
};

/**
 * Anonymize all report text. In reality we are explicit about what we redact,
 * for now this includes:
 *
 * - report.title
 * - report.content.data (NOTE: We don't redact filename)
 *
 * @param {Immutable.Map} report
 * @param {Immutable.Map} redactionMap
 * @param {bool} toHash (This will be true only when saving/submitting reports)
 *
 * @return {Immutable.Map} anonymizedReport
 */
type RedactAll = (a: {
  report: Map<any, any>,
  redactionMap: Map<any, any>,
  toHash: boolean,
}) => Map<any, any>;
export const redactAll: RedactAll = ({ report, redactionMap, toHash }) => {
  // Short circuit if there is nothing to redact.
  if (!redactionMap.size) return report;

  return report.map((val, key) => {
    switch (key) {
    case 'title':
      return parseRedacted(val, redactionMap, toHash);
    case 'content':
      return val.set('data', parseRedacted(val.get('data'), redactionMap, toHash));
    default:
      return val;
    }
  });
};

/**
 * Return an inclusion predicate function to test if x is included within the
 * list passed to oneOf
 */
export const oneOf = (...args: Array<any>): Predicate<string> => (x) => {
  return args.indexOf(x) > -1;
};

/**
 * Exclude the list of keys from a merge of two objects. Meaning the value at
 * any of the keys passed in will not be merged. The result of the merge will be
 * whatever the value was on the original object.
 *
 * NOTE: Returning undefined will let result in the default behavior.
 *
 * Produces a custom mergeWith customizer for use with lodash:
 * See: https://github.com/lodash/lodash/blob/4.16.1/lodash.js#L13288
 */
export const exclude = (...keys: Array<string>): Function => {
  return (objValue, srcValue, key) => {
    if (keys.includes(key)) return objValue;
  };
};

const _get = (attr: string | number, obj: Object) =>
  obj.get ? obj.get(attr) : obj[attr];

/**
 * Create a sorting function for immutable iterables.
 */
export const sortBy = (key: string, order: 'ASC' | 'DESC') => (a: Iterable<string, *>, b: Iterable<string, *>): number => {
  let sortVal = 0;
  let compareA = _get(key, a);
  let compareB = _get(key, b);

  if (!compareA && !compareB) {
    sortVal = 0;
  } else if (!compareA) {
    sortVal = 1;
  } else if (!compareB) {
    sortVal = -1;
  } else {
    // Make sure to convert to lowercase if we're dealing with strings. We
    // do not want a case-sensitive comparator
    if (isString(compareA)) compareA = toLower(compareA);
    if (isString(compareB)) compareB = toLower(compareB);

    // The comparator
    sortVal = (compareA < compareB) ? 1 : -1;
  }

  if (order === 'DESC') sortVal = sortVal * -1;

  return sortVal;
};

/* NOTE: Similar to above but accepts any iterable entity and an options object and
 * applies sorting and filtering based on the options to the iterable. Options
 * include a search query, a soryBy field key (e.g. name, text, etc.), and
 * an order: ASC | DESC. This method is used for some of our table components,
 * e.g. Enclaves, Malware Terms, etc.
 *
 * TODO: There are still very similar methods in User, Companies getters... it would
 * be nice to refactor them all to use this one common method. There is some logic
 * specific to those modules which is why I didn't refactor them already with this.
 */
const OptionsType = Record({ // TODO: I just want this as a type...
  query: '',
  sortBy: 'name',
  sortOrder: 'DESC',
});
type AnyIterableType = Map<any, any> | List<any> | OrderedMap<any, any>;
type ApplySearchFnType = (entities: AnyIterableType, options: OptionsType) => AnyIterableType;
export const applySearchAndSortOptions: ApplySearchFnType = (entities, options) => {
  const query = options.get('query');
  return entities
    .filter(entity => {
      const regex = new RegExp(escapeRegExp(query), 'i');

      // Test all top-level string values
      return entity
        .valueSeq()
        .filter(isString)
        .some(x => x.search(regex) > -1);
    })
    .sort((a, b) => {
      const sortBy = options.get('sortBy');
      let sortVal = 0;
      let compareA = a.get(sortBy);
      let compareB = b.get(sortBy);

      if (!compareA && !compareB) {
        sortVal = 0;
      } else if (!compareA) {
        sortVal = 1;
      } else if (!compareB) {
        sortVal = -1;
      } else {
        // Make sure to convert to lowercase if we're dealing with strings. We
        // do not want a case-sensitive comparator
        if (isString(compareA)) compareA = compareA.toLowerCase();
        if (isString(compareB)) compareB = compareB.toLowerCase();

        // The comparator
        sortVal = (compareA < compareB) ? 1 : -1;
      }

      if (options.get('sortOrder') === 'DESC') sortVal = sortVal * -1;

      return sortVal;
    })
    .toList();
};

/* Custom sort to group list of items (e.g. tags) with an enclaveId by enclave. */
type Tag = Map<string, string>;
export const sortByEnclaveId = (a: Tag, b: Tag): number => {
  return a.get('enclaveId').localeCompare(b.get('enclaveId'));
};

/**
 * Return a new date with any time of day information stripped away. Useful if
 * we want to reduce the accuracy of dates. We had considered using this in
 * timeline filtering to only filter by day and not actual timestamp.
 */
export const trimDateTime = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDay());

/**
 * A safe toJS function. Call it on anything!
 */
export const toJS = (x: any) => IO.of(x).map(x => x.toJS()).foldOr(() => x);

/**
 * Check if something is an Immutable object.
 */
export const isImmutable = (x: any): boolean => {
  return Iterable.isIterable(x);
};

export const validatePasswords = (password: string, passwordConfirm: string): Array<string> => {

  const errors = validatePassword(password);

  if (!errors.length) {
    if (!passwordConfirm) {
      errors.push('Please enter the confirmation password');
    } else if (password !== passwordConfirm) {
      errors.push('Passwords do not match');
    }
  }

  return errors;
};

export const validatePassword = (password: string): Array<string> => {

  const errors = [];

  if (!password) {
    errors.push('Please enter a password');
  } else {

    let requiredCategoriesMessge = [];

    // Category 1: non numerics
    if (!password.match('[^a-zA-Z0-9]')) {
      requiredCategoriesMessge.push('- non-alphabetic characters (!,$,#,%)');
    }

    // Category 2: numbers
    if (!password.match('[0-9]')) {
      requiredCategoriesMessge.push('- at least one number (0-9)');
    }

    // Category 3: lower case characters
    if (!password.match('[a-z]')) {
      requiredCategoriesMessge.push('- at least one lowercase letter (a-z)');
    }

    // Category 4: capital case characters
    if (!password.match('[A-Z]')) {
      requiredCategoriesMessge.push('- at least one uppercase letter (A-Z)');
    }

    // From the total of 4 categories we allow only 1 category not to be satisfied
    if (requiredCategoriesMessge.length > 1) {
      const categoriesToSatisfy = 3 - requiredCategoriesMessge.length;
      errors.push(`Your password must contain ${ categoriesToSatisfy } of the following: \n${ requiredCategoriesMessge.join('\n') }`);
    }

    if (password.length < 12) {
      errors.push('Password must be at least 12 characters long');
    }
  }

  return errors;
};

export const getFunctionNamesInObject = compose(keys, pickBy(is(Function)));

export const resolveAPI = (documentPath: string): string => {
  if(process.env.API_HOST && process.env.API_HOST.length > 0) {
    return process.env.API_HOST + documentPath;
  }
  return documentPath;
};



// WEBPACK FOOTER //
// ./web_modules/trustar/utils/utils.js